import { DeleteIcon, DragIndicatorIcon } from '@/assets/icons'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React from 'react'
import { DraftDestination } from './core/draft'
import { daysLabel } from './core/presentation'
import {
	DayCount,
	DragHandle,
	PlusButton,
	RemoveButton,
	Row,
	RowBand,
	RowControls,
	RowError,
	RowMeta,
	RowName,
	RowText,
	StepperButton,
} from './WhereStep.styles'
import { iconSizes } from '@/theme/tokens'

/** What the edit flow adds to a row: what it holds, and what its edit would break. */
export interface DestinationRowDetail {
	/** A line under the name — "3 events". */
	meta?: string
	/** An error under the band — the row's days still hold events the edit would strand. */
	error?: string
	/** Why Remove is refused for this row — the destination still holds events. Unset lets it go. */
	removeRefusal?: string
}

interface DestinationRowProps extends Omit<DestinationRowDetail, 'removeRefusal'> {
	destination: DraftDestination
	onIncrement: () => void
	onDecrement: () => void
	onRemove: () => void
}

/**
 * One destination in the Where step: its name, the day stepper, remove, and
 * the drag handle that reorders it (pointer or keyboard, via dnd-kit). The
 * edit flow adds a meta line and, when the row's edit can't be saved, an
 * error under it.
 */
const DestinationRow: React.FC<DestinationRowProps> = ({
	destination,
	meta,
	error,
	onIncrement,
	onDecrement,
	onRemove,
}) => {
	const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
		useSortable({ id: destination.key })
	const name = destination.place.name

	return (
		<Row
			ref={setNodeRef}
			$transform={CSS.Transform.toString(transform)}
			$transition={transition}
			$isDragging={isDragging}
		>
			<RowBand>
				<RowText>
					<RowName>{name}</RowName>
					{meta && <RowMeta>{meta}</RowMeta>}
				</RowText>
				<RowControls>
					<StepperButton
						type="button"
						aria-label={`Fewer days in ${name}`}
						disabled={destination.dayCount <= 1}
						onClick={onDecrement}
					>
						−
					</StepperButton>
					<DayCount>{daysLabel(destination.dayCount)}</DayCount>
					<PlusButton type="button" aria-label={`More days in ${name}`} onClick={onIncrement}>
						+
					</PlusButton>
					<RemoveButton type="button" aria-label={`Remove ${name}`} onClick={onRemove}>
						<DeleteIcon sx={{ fontSize: iconSizes.md }} />
					</RemoveButton>
					<DragHandle
						ref={setActivatorNodeRef}
						type="button"
						aria-label={`Reorder ${name}`}
						{...attributes}
						{...listeners}
					>
						<DragIndicatorIcon sx={{ fontSize: iconSizes.md }} />
					</DragHandle>
				</RowControls>
			</RowBand>
			{error && <RowError role="alert">{error}</RowError>}
		</Row>
	)
}

export default DestinationRow
